import React, { useEffect, useState } from "react";
import { Typography, Dropdown, Space } from "antd";
import { DownOutlined } from "@ant-design/icons";
import { useLocation, useNavigate } from "react-router-dom";
import { useMercuryContext, useMercuryDispatch } from "context";
import { ROUTES } from "common/constants/routes";
import { normalizeDashboardRoute } from "common/utils/dashboardRoutes";
import { usePermissions } from "hooks/usePermissions";

const { Text } = Typography;
const STORAGE_KEY = "selectedCA";

export const extractAppFromPath = (path: string, apps: any[]) => {
  const segment = path.split("/")[1]?.toLowerCase();
  return apps.some((a) => a.name.toLowerCase() === segment) ? segment : null;
};

export const findAppByName = (apps: any[], name: string) => {
  return apps.find((a) => a.name.toLowerCase() === name.toLowerCase());
};

export const getDefaultRoute = (
  app: any,
  dashboards: any[],
  getAccessibleRoutesForApp: Function,
  defaultPaths: Record<string, string>
) => {
  const appDashboards = dashboards
    .filter((d: any) => d.customerApplication === app._id)
    .map((d: any) => ({ ...d, route: normalizeDashboardRoute(d.route) }))
    .filter((d: any) => d.route);

  // permission-based routes so direct dashboard assignments do not
  // override normal navigation flow
  const accessRoutes = getAccessibleRoutesForApp(app.name);
  if (accessRoutes.length > 0) return accessRoutes[0];

  if (appDashboards.length > 0) {
    appDashboards.sort((a: any, b: any) => a.name.localeCompare(b.name));
    return appDashboards[0].route;
  }

  // Fallback
  const fallback = defaultPaths[app.name.toLowerCase()];
  return fallback ?? ROUTES.MAIN;
};

const DEFAULT_PATHS: Record<string, string> = {
  google: ROUTES.GOOGLE_MAIN,
  olive: ROUTES.OLIVE_MAIN,
};

const AppSwitcher: React.FC = () => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useMercuryDispatch();
  const { hasPermissions } = usePermissions();

  const { selectedApp, userDetails = {} } = useMercuryContext();
  const apps = userDetails.customerApplicationDetails || [];
  const dashboards = userDetails.assignedDashboards || [];

  const [items, setItems] = useState([]);

  useEffect(() => {
    setItems(
      apps
        .filter((a: any) => !a.internal)
        .map((a: any) => ({ key: a._id, label: a.name }))
    );
  }, [apps]);

  const PERMISSION_BASED_ROUTES: Record<string, any[]> = {
    olive: [
      {
        permission: {
          customerApplication: "OLiVE",
          permissionName: "Batches",
          action: "read",
        },
        route: ROUTES.OLIVE_BATCHES,
      },
      {
        permission: {
          customerApplication: "OLiVE",
          permissionName: "Scripts",
          action: "read",
        },
        route: ROUTES.OLIVE_SCRIPTS,
      },
    ],
  };

  const FALLBACK_APP_ROUTES: Record<string, string[]> = {
    google: [ROUTES.GOOGLE_PROJECTS, ROUTES.GOOGLE_PLPS],
    olive: [],
  };

  const getAccessibleRoutesForApp = (appName: string) => {
    const normalized = appName.toLowerCase();

    if (PERMISSION_BASED_ROUTES[normalized]) {
      const allowed = PERMISSION_BASED_ROUTES[normalized]
        .filter((e) => hasPermissions([e.permission]))
        .map((e) => e.route);

      if (allowed.length > 0) return allowed;
    }

    return FALLBACK_APP_ROUTES[normalized] ?? [];
  };

  const navigateToDefault = (appId?: string) => {
    let app =
      apps.find((a: any) => a._id === appId) ||
      apps.find((a: any) => a.name !== "Mercury");

    if (!app) return;

    if (app.name === "Mercury") {
      app = apps.find((a: any) => a.name !== "Mercury");
      if (!app) return;
    }

    const route = getDefaultRoute(
      app,
      dashboards,
      getAccessibleRoutesForApp,
      DEFAULT_PATHS
    );

    navigate(route, { replace: true });
  };

  const onClick = ({ key }: any) => {
    localStorage.setItem(STORAGE_KEY, key);
    dispatch({ type: "SET_SELECTED_APP", payload: key });
    navigateToDefault(key);
  };

  const isSameAppRoute = (path: string, appName: string) => {
    return path.toLowerCase().startsWith(`/${appName.toLowerCase()}`);
  };

  useEffect(() => {
    if (!apps.length) return;

    const pathApp = extractAppFromPath(location.pathname, apps);
    if (!pathApp) return;

    const targetApp = findAppByName(apps, pathApp);
    if (!targetApp) return;

    // Already selected do nothing
    if (selectedApp !== targetApp._id) {
      // Switch app
      localStorage.setItem(STORAGE_KEY, targetApp._id);
      dispatch({ type: "SET_SELECTED_APP", payload: targetApp._id });
    }

    if (isSameAppRoute(location.pathname, targetApp.name)) {
      return;
    }

    navigateToDefault(targetApp._id);
  }, [location.pathname, apps]);

  useEffect(() => {
    if (!apps.length) return;

    const pathApp = extractAppFromPath(location.pathname, apps);
    if (pathApp) return;

    const stored = localStorage.getItem(STORAGE_KEY);
    if (stored && !selectedApp) {
      dispatch({ type: "SET_SELECTED_APP", payload: stored });
      navigateToDefault(stored);
    }
  }, [apps, location.pathname]);

  // Handle "/" redirect to first accessible CA
  useEffect(() => {
    if (!apps.length) return;

    const path = location.pathname;

    if (path !== ROUTES.MAIN && path !== "") return;

    if (selectedApp && apps.some((a: any) => a._id === selectedApp)) {
      navigateToDefault(selectedApp);
      return;
    }

    // Else fallback to first available CA
    const firstApp = apps[0];
    if (firstApp) {
      localStorage.setItem(STORAGE_KEY, firstApp._id);
      dispatch({ type: "SET_SELECTED_APP", payload: firstApp._id });
      navigateToDefault(firstApp._id);
    }
  }, [apps, selectedApp, location.pathname]);

  const selectedAppObj = apps.find((a: any) => a._id === selectedApp);

  if (!items.length) return null;

  if (items.length === 1) {
    return <Text className="appSwitcherSingle">{selectedAppObj?.name}</Text>;
  }

  return (
    <Dropdown
      menu={{ items, onClick }}
      trigger={["click"]}
      className="appSwitcher"
      overlayClassName="appSwitcherDropdown"
    >
      <Space>
        {selectedAppObj?.name || "Select App"} <DownOutlined />
      </Space>
    </Dropdown>
  );
};

export default AppSwitcher;
